import { Col, Row } from 'antd';
import { useMemo } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import { type SimpleVehicleManagementModuleSpecsFragment } from '../../../../../api/fragments/SimpleVehicleManagementModuleSpecs';
import {
    type CreateSimpleVehicleManagementModuleMutation,
    type CreateSimpleVehicleManagementModuleMutationVariables,
    CreateSimpleVehicleManagementModuleDocument,
} from '../../../../../api/mutations/createSimpleVehicleManagementModule';
import { TemplateType, type SimpleVehicleManagementSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import validators from '../../../../../utilities/validators';
import { type CompanySpecs } from '../../../AddModulePage/getAvailableModules';
import SimpleVehicleManagementModulePage, {
    type SimpleVehicleManagementModuleProps,
} from '../../SimpleVehicleManagementModulePage';
import useModuleOptions from './shared/useModuleOptions';
import { type ModuleImplementation } from './types';

type SimpleVehicleManagementModuleImplementation = ModuleImplementation<
    SimpleVehicleManagementModuleSpecsFragment,
    SimpleVehicleManagementSettings
>;

type FormProps = {
    company: CompanySpecs;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredStringEnum('defaultTemplate', TemplateType)
);

const computeTemplateList = (name: string, t: TFunction) => {
    if (name === 'BMW') {
        return [
            {
                value: TemplateType.AfcTemplate,
                label: t('moduleDetails:explanations.templates.AFCTemplate.label'),
            },
            {
                value: TemplateType.BmwTemplate,
                label: t('moduleDetails:explanations.templates.BMWTemplate.label'),
            },
        ];
    }

    return [
        {
            value: TemplateType.AfcTemplate,
            label: t('moduleDetails:explanations.templates.AFCTemplate.label'),
        },
    ];
};

const Form: SimpleVehicleManagementModuleImplementation['FormComponent'] = ({ company }: FormProps) => {
    const { t } = useTranslation(['moduleDetails']);
    const templateOptions = useMemo(() => computeTemplateList(company.displayName, t), [company.displayName, t]);
    const options = useModuleOptions(company, { addNoneOptions: { porscheMasterData: true } });

    return (
        <Row gutter={10}>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <SelectField
                    {...t('moduleDetails:fields.importExportTemplate', { returnObjects: true })}
                    name="defaultTemplate"
                    options={templateOptions}
                    required
                    showSearch
                />
            </Col>
            {/* only has created Porsche MasterData module, will show this selection */}
            {options.porscheMasterData.length > 1 && (
                <Col md={8} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.porscheMasterDataModule', { returnObjects: true })}
                        name="porscheMasterDataModuleId"
                        options={options.porscheMasterData}
                        showSearch
                    />
                </Col>
            )}
        </Row>
    );
};

const implementation: ModuleImplementation<
    SimpleVehicleManagementModuleSpecsFragment,
    SimpleVehicleManagementSettings
> = {
    PageComponent: ({ module }: SimpleVehicleManagementModuleProps) => (
        <SimpleVehicleManagementModulePage module={module} />
    ),
    getTitle: t => t('moduleDetails:implementations.SimpleVehicleManagementModule.className'),
    FormComponent: Form,
    getValidator: () => validator,
    create: async (apolloClient, companyId, values) =>
        apolloClient
            .mutate<CreateSimpleVehicleManagementModuleMutation, CreateSimpleVehicleManagementModuleMutationVariables>({
                mutation: CreateSimpleVehicleManagementModuleDocument,
                variables: { companyId, settings: values },
            })
            .then(({ data }) => data.module),
    initialValues: {
        defaultTemplate: TemplateType.AfcTemplate,
        porscheMasterDataModuleId: null,
    },
};

export default implementation;
